import { useAuthStore } from '@/stores/auth.store';
import type { AuthSession } from '@/types/auth';
import { toApiError } from '@/services/http/api-error';
import { authApi } from './auth.api';
import { SIGNED_IN_KEY, sessionHint } from './session-hint';

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Runs one refresh at a time across every tab of this browser. The tabs share the refresh
 * cookie and the server rotates it on each use, so two tabs refreshing together would look
 * like a stolen token; the server also forgives a near-simultaneous one (30 s).
 */
async function oneTabAtATime<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? await locks.request('bovid-admin-refresh', run) : run();
}

async function exchange(): Promise<string | null> {
  if (!sessionHint.exists()) {
    useAuthStore.getState().clear();
    return null;
  }
  try {
    const legacy = sessionHint.legacyToken();
    const remember = legacy ? legacy.remember : sessionHint.remember();
    const session = await oneTabAtATime(() =>
      authApi.refresh({ rememberMe: remember, ...(legacy ? { refreshToken: legacy.token } : {}) }),
    );
    if (legacy) {
      // The server has set the cookie: the token leaves browser storage.
      sessionHint.clearLegacyToken();
      sessionHint.save(legacy.remember);
    }
    useAuthStore.getState().setSession(session);
    return session.accessToken;
  } catch (error) {
    // A 4xx means the session is over (expired, revoked, signed out elsewhere). On a network
    // or server error, or "too many requests", keep the hint, so a reload can try again.
    const { status } = toApiError(error);
    if (status >= 400 && status < 500 && status !== 429) sessionHint.clear();
    useAuthStore.getState().clear();
    return null;
  }
}

/**
 * A new access token from the refresh cookie. Single-flight within the tab: concurrent 401s
 * share one call. Resolves to the new access token, or null when the session is over.
 */
export function refreshSession(): Promise<string | null> {
  inflightRefresh ??= exchange().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

let listening = false;

/** Restores the session on page load. Call once, before the first render settles. */
export async function bootstrapSession(): Promise<void> {
  if (!listening && typeof window !== 'undefined') {
    listening = true;
    // Signing out in one tab signs out the others at once.
    window.addEventListener('storage', (event) => {
      if (event.key === SIGNED_IN_KEY && event.newValue === null) useAuthStore.getState().clear();
    });
  }
  if (useAuthStore.getState().status !== 'bootstrapping') return;
  await refreshSession();
}

export function startSession(session: AuthSession, remember: boolean): void {
  sessionHint.save(remember);
  useAuthStore.getState().setSession(session);
}

/** Ends the session locally at once; revoking it on the server is best effort. */
export async function endSession(): Promise<void> {
  const signedIn = useAuthStore.getState().status === 'authenticated' || sessionHint.exists();
  sessionHint.clear();
  useAuthStore.getState().clear();
  if (!signedIn) return;
  try {
    await authApi.logout();
  } catch {
    // Already signed out locally; the server session expires on its own.
  }
}
